import { todayStr } from "./date";
import { programmeFor } from "./programme";
import { weekSpan } from "./programmeView";
import { hasTreadmill } from "./treadmill";
import { walkSecs } from "./walk";
import { weekDone, weekRows, type WeekRow } from "./week";
import type { KayaState, SessionKey } from "./types";

/*
 * The completed week (ADR-066; research §3.11): what this week has had, as "of" counts and a tick
 * a day — never a percentage, a streak or "behind". A day not done is a dash, the same as a day
 * with nothing planned; nothing is red. The owner's streak stays where it was (Today, Progress).
 */

export type TickState = "done" | "planned" | "today" | "none";

export interface WeekTick {
  kind: "strength" | "sessions" | "walks" | "treadmill";
  label: string;
  done: number;
  /** what was planned; null where nothing is (the Kaya programme's walks and treadmill) */
  of: number | null;
  /** Monday to Sunday */
  days: TickState[];
}

export interface CompletedWeek {
  /** "Mon 2 – Sun 8 Nov" */
  span: string;
  rows: WeekTick[];
  /** "Strength 2 of 3 · Walks 4 of 5" */
  line: string;
}

/** A walk counts once it lasts this long (WHO: bouts of any length count; ten minutes is the
 *  walk after a meal, `programmeView.ts::MEAL_WALK_MIN`). */
export const WALK_COUNTS_SECS = 10 * 60;

const STRENGTH_KEYS: readonly SessionKey[] = ["SB", "CH"];

/** The tick for one day: done beats everything; a planned day is today's filled ring, a ring to
 *  come, or a dash once it has gone by — the same dash as a day with nothing on it. */
function tick(r: WeekRow, done: boolean, planned: boolean, today: string): TickState {
  if (done) return "done";
  if (!planned || r.beforePlan) return "none";
  if (r.ds === today) return "today";
  return r.ds > today ? "planned" : "none";
}

/** "4 of 5", "3" where nothing is planned, "6 walks · 5 planned" once the count passes the plan. */
export function tickCount(t: Pick<WeekTick, "label" | "done" | "of">): string {
  if (t.of === null) return String(t.done);
  if (t.done > t.of) return `${t.done} ${t.label.toLowerCase()} · ${t.of} planned`;
  return `${t.done} of ${t.of}`;
}

function rowsLine(rows: WeekTick[]): string {
  return rows.map((t) => `${t.label} ${tickCount(t)}`).join(" · ");
}

/** The week holding `today` for either programme. `simple` is the phone's player mode, passed to
 *  `weekRows` so a day's minutes match Train. */
export function completedWeek(S: KayaState, today = todayStr(), simple?: boolean): CompletedWeek {
  const rows = weekRows(S, today, simple);
  const future = (r: WeekRow) => r.ds > today;
  const out: WeekTick[] = [];

  if (S.profile.training && programmeFor(S, today) === "steady") {
    const meals = (S.profile.training.mealWalks?.length ?? 0) > 0;
    const strength = rows.map((r) => STRENGTH_KEYS.includes(r.key) && !r.beforePlan);
    const strengthDone = rows.map((r, i) => strength[i] && (r.state === "done" || r.state === "rescued"));
    out.push({
      kind: "strength",
      label: "Strength",
      done: strengthDone.filter(Boolean).length,
      of: strength.filter(Boolean).length,
      days: rows.map((r, i) => tick(r, strengthDone[i], strength[i], today)),
    });
    // a walk day is WK, or MJ when the answers have walks after meals; walks are uncapped — a
    // walk on a day with none planned still counts, and the count may pass the plan
    const walkDay = rows.map((r) => !r.beforePlan && (r.key === "WK" || (r.key === "MJ" && meals)));
    const walked = rows.map(
      (r) => !future(r) && (walkSecs(S.logs[r.ds]?.walks) >= WALK_COUNTS_SECS || (r.key === "WK" && r.state === "done"))
    );
    const of = walkDay.filter(Boolean).length;
    out.push({
      kind: "walks",
      label: "Walks",
      done: walked.filter(Boolean).length,
      // nothing planned reads as a plain count, never "2 of 0"
      of: of || null,
      days: rows.map((r, i) => tick(r, walked[i], walkDay[i], today)),
    });
  } else {
    const wk = weekDone(rows);
    const planned = rows.map((r) => r.key !== "R" && !r.beforePlan);
    out.push({
      kind: "sessions",
      label: "Sessions",
      done: wk.done,
      of: wk.of,
      days: rows.map((r, i) => tick(r, r.state === "done" || r.state === "rescued", planned[i], today)),
    });
    const walked = rows.map((r) => !future(r) && (S.logs[r.ds]?.walks ?? []).some((w) => w.secs >= WALK_COUNTS_SECS));
    out.push({
      kind: "walks",
      label: "Walks",
      done: walked.filter(Boolean).length,
      of: null,
      days: rows.map((r, i) => (walked[i] ? "done" : r.ds === today ? "today" : "none")),
    });
    if (hasTreadmill(S)) {
      const ran = rows.map((r) => !future(r) && !!S.logs[r.ds]?.treadmill);
      out.push({
        kind: "treadmill",
        label: "Treadmill",
        done: ran.filter(Boolean).length,
        of: null,
        days: rows.map((r, i) => (ran[i] ? "done" : r.ds === today ? "today" : "none")),
      });
    }
  }
  return { span: weekSpan(today), rows: out, line: rowsLine(out) };
}

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** A row in words for a screen reader: "Walks, 4 of 5. Done Mon, Tue, Thu and Sat. Today: Sun." */
export function tickAria(t: WeekTick): string {
  const list = (s: TickState) => t.days.map((d, i) => (d === s ? DAY_SHORT[i] : null)).filter((x): x is string => !!x);
  const words = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}` : xs[0]);
  const parts = [`${t.label}, ${tickCount(t)}.`];
  const done = list("done");
  const today = list("today");
  const planned = list("planned");
  if (done.length) parts.push(`Done ${words(done)}.`);
  if (today.length) parts.push(`Today: ${words(today)}.`);
  if (planned.length) parts.push(`To come: ${words(planned)}.`);
  return parts.join(" ");
}
